import type { TerrainKind } from '../track/elevation';
import type { MedalTimes } from '../track/types';
import type { BoardEntry } from '../net/protocol';
import { PosterArt } from '../render/posterArt';
import { figurePictures, helmetPicture, podiumFigures } from '../render/podiumArt';
import { FEEDBACK_MAX } from '../net/beta';
import { NAME_MAX } from '../net/protocol';
import type { BringServer } from '../net/discord';
import { BoardPanel, type BoardScope, DISCORD_GLYPH, scopeSwitch } from './board';
import type { PosterMotif, PosterPose, PosterStyle } from './posterStyle';
import { bootDone, bootLoading } from './boot';
import { el, formatLap, ordinal } from './format';

/**
 * The title card, as a vintage race poster: one centred sheet with four sides
 * (the day's race, how to play, today's board, a friend's challenge), a cream
 * strip along its foot with the race button, the driving lesson and the
 * switches, and on wide screens two smaller cards beside it: yesterday's
 * podium and today's board at a glance. On narrower screens the podium folds
 * into the sheet and the board into a line under the medals.
 */
export type PosterView = 'main' | 'how' | 'board' | 'challenge' | 'feedback' | 'settings';

/**
 * A friend's challenge, on its own side before anything else, set out like a
 * race programme for someone who's never played: who challenged you, what the
 * game is, the circuit and the car, the time to beat, the day's board, and
 * what happens next.
 */
export interface ChallengeIntro {
  /** "camilo has challenged you!" */
  label: string;
  /** The big lettering: "grand premio", or "this race is over" for an old link. */
  title?: string;
  /** What the game is, in a line. */
  pitch: string;
  circuit: { label: string; name: string; tag: string; outline: string };
  car: { label: string; name: string; year: number; blurb: string };
  beat: { label?: string; time: string; who: string };
  board: { title: string; rows: { rank: number; name: string; time: string; them: boolean }[] } | null;
  steps: { title: string; text: string }[];
  /** In place of the steps: what happened (an old link's race is over) and what to do now. */
  note?: string;
  watch: string;
  race: string;
  /** Where the second button goes: racing this lap, or (an old link) today's circuit. */
  raceAction?: PosterActionId;
}

/** The poll's answers: enjoying it (or not, or not now), and after a no, what's missing. */
export type PollAnswer = 'up' | 'down' | 'skip' | 'hard' | 'controls' | 'slow' | 'samey';
const POLL_WHY: [PollAnswer, string][] = [
  ['hard', 'too hard'],
  ['controls', 'the controls'],
  ['slow', 'runs slowly'],
  ['samey', 'gets samey'],
];

/** The poster's buttons: race (or resume), the board, watch a lap, send your lap back. */
export type PosterActionId = 'race' | 'board' | 'watch' | 'sendBack' | 'today';
export interface PosterAction {
  id: PosterActionId;
  label: string;
}

/** Yesterday's podium: where it was, and the top three (in order; fewer on a quiet day). */
export interface PodiumInfo {
  /** "circuito di valdoro · the cigar 1954" */
  where: string;
  places: { place: number; name: string; ms: number }[];
  /** The morning paper can be read again (this player has one today). */
  paper?: boolean;
}

/** A friend driving right now in the same Discord server: who (their key in the room), and what they're up to ("lap 4"). */
export interface LiveDriver {
  key: string;
  name: string;
  status: string;
  paused?: boolean;
}

/** Today's board at a glance. */
export interface TodayInfo {
  total: number;
  /** The top of the board (up to three). */
  top: BoardEntry[];
  /** Your next target (the one lap ahead of yours you may watch), and your own row. */
  target: BoardEntry | null;
  you: BoardEntry | null;
  /** Under the rows: "no times yet · be the first", or how to join in. */
  note: string | null;
  /** Your lap is on the board and can go out as a challenge. */
  challenge: boolean;
  /** Inside a Discord server: the switch between its board and everyone's. */
  scope?: BoardScope | null;
}

/**
 * The driving lesson's ticket beside the race button: big for someone new ('new'), or for someone who keeps
 * restarting ('nudge'); smaller once they've finished laps ('regular'); just the helmet once they've won a
 * medal ('small'); none (a preview, a friend's challenge).
 */
export type LessonTicket = 'new' | 'nudge' | 'regular' | 'small';

export interface TitleInfo {
  circuit: string;
  /** "monday 28 september" */
  day: string;
  /** "closes in 7h 36m", "board closed · practice only", or null for another day. */
  closes: string | null;
  car: { name: string; year: number; blurb: string; preview: boolean };
  terrain: TerrainKind;
  /** A figure of eight: the track crosses over itself. */
  crossover: boolean;
  /** The day's showpiece (v3 circuits). */
  signature?: string;
  /** The day's print: colours, the car's angle, the motif behind it. */
  style: PosterStyle;
  medals: MedalTimes;
  /** The medals won today, on this circuit and car. */
  won: { bronze: boolean; silver: boolean; gold: boolean };
  bestMs: number | null;
  /** Where your best stands on today's board (null: not on it, or the board isn't open). */
  standing?: { rank: number; total: number } | null;
  /** Drivers on today's open board so far (null: it isn't open here, or hasn't loaded). */
  drivers?: number | null;
  /** Your best isn't on the board yet and wants a name: offer to save it. */
  saveable?: boolean;
  /** Never finished a lap on this browser: the controls go in the small print. */
  firstTime: boolean;
  /** How you're playing: on a phone, with keys, or with a controller. */
  controls: 'touch' | 'keys' | 'pad';
  /** On a phone: the gas and brake as buttons (the default) or a slider. */
  pedals?: 'buttons' | 'slider';
  paused: boolean;
  /** The buttons, in order: the first is the big one. */
  actions: PosterAction[];
  /**
   * Racing someone's lap: the kicker ("a challenge from camilo"), its line
   * ("camilo · 41.843 🥇 · P3 of 27"), a one-line reminder of what's going on,
   * and before watching, the whole thing step by step on its own side.
   */
  challenge?: { kicker: string; line: string; hint?: string | null; intro?: ChallengeIntro | null; drop?: string | null } | null;
  /** Yesterday's podium, each lap there to watch (today's poster only). */
  podium?: PodiumInfo | null;
  /** Today's board at a glance (today's poster only). */
  today?: TodayInfo | null;
  /** Your best lap can be shared: copy it, or send it (where the phone has a share sheet). */
  share?: { canSend: boolean } | null;
  /** A short note ("that link has expired"). */
  notice?: string | null;
  /** A nudge in big print above the buttons ("your move: set a lap, then send it to camilo"). */
  nudge?: string | null;
  /** Another day's circuit is on: a way back to today's. */
  otherDay?: boolean;
  /** Ask whether they're enjoying it (once, after a few laps). */
  poll?: boolean;
  /** The driving lesson's ticket, or null. */
  lesson?: LessonTicket | null;
  /**
   * You've finished a lap: "leaderboard" takes the place beside race (unless the lesson is being
   * suggested), and the lesson moves to the switches as a helmet.
   */
  boardButton?: boolean;
  /** The driving line and braking zones on the road are on (null: not offered, as in a car preview). */
  line?: boolean | null;
  /** The camera's shake (speed shake, grass bob, wall jolt): on unless switched off. */
  shake?: boolean;
  /** On the website: the line over race that leads to the Discord side (after a first lap, when there's an app to add). */
  discordDoor?: boolean;
  /** On the website, with a Discord app: signing in with it (`as`: the Discord name signed in as, or null). Left out: no switch. */
  signIn?: { as: string | null };
  /** Inside Discord: friends can be invited to the game (Discord's own invite window). */
  invite?: boolean;
}

const TERRAIN_TAG: Record<TerrainKind, string> = { flat: '', rolling: 'rolling hills', hilly: 'hilly', spa: 'spa-style dip' };
const SIGNATURE_TAG: Record<string, string> = {
  esses: 'the esses',
  corkscrew: 'the corkscrew',
  banking: 'banked curve',
  karussell: 'the carousel',
  crossover: 'figure of eight',
  eauRouge: 'the valley',
  climb: 'uphill hairpin',
  streets: 'street section',
  tripleApex: 'triple apex',
  stadium: 'the stadium',
  jumps: 'the jumps',
  mountain: 'the mountain',
};

/** The circuit's one-line character: its showpiece, or its shape, or its hills. */
export function circuitTag(signature: string | undefined, crossover: boolean, terrain: TerrainKind): string {
  return (signature && SIGNATURE_TAG[signature]) || (crossover ? 'figure of eight' : TERRAIN_TAG[terrain]);
}

/** The poster's glyphs, printed in the ink of the day. */
/** A server's picture, round as in Discord's list, or its initials on red. */
function serverMark(parent: HTMLElement, s: BringServer): HTMLElement {
  const m = el('span', 'srv', parent);
  if (s.icon) {
    const img = document.createElement('img');
    img.alt = '';
    img.src = `https://cdn.discordapp.com/icons/${s.id}/${s.icon}.png?size=128`;
    m.appendChild(img);
  } else {
    m.textContent =
      s.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => [...w][0])
        .join('')
        .toUpperCase() || '?';
  }
  return m;
}

const GLYPH = {
  soundOn:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="fill" d="M3 9h4l5-4v14l-5-4H3z"/><path class="line" d="M15.5 8.5a5 5 0 0 1 0 7M18 6a8.5 8.5 0 0 1 0 12"/></svg>',
  soundOff:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="fill" d="M3 9h4l5-4v14l-5-4H3z"/><path class="line" d="M15.5 9.5l5 5M20.5 9.5l-5 5"/></svg>',
  line: '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="line" d="M4 20c2-6 4-9 8-9s6-3 8-7"/><path class="dash" d="M4 20c2-6 4-9 8-9s6-3 8-7"/></svg>',
  /** A phone's gas and brake: two buttons (BRAKE over GAS), or a slider. */
  pedalButtons:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><circle class="line" cx="12" cy="6.5" r="3.6"/><circle class="fill" cx="12" cy="17" r="4.6"/></svg>',
  pedalSlider:
    '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="line" d="M12 2.5v19"/><circle class="fill" cx="12" cy="9" r="4.2"/></svg>',
  /** Discord's logo (its brand mark, unchanged): white on the red add buttons, Discord's blurple elsewhere. */
  discord: DISCORD_GLYPH,
  /** A screen with a tremor either side. */
  gear: '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="line" d="M10.3 3h3.4l.5 2.4 1.6.7 2.1-1.4 2.4 2.4-1.4 2.1.7 1.6 2.4.5v3.4l-2.4.5-.7 1.6 1.4 2.1-2.4 2.4-2.1-1.4-1.6.7-.5 2.4h-3.4l-.5-2.4-1.6-.7-2.1 1.4-2.4-2.4 1.4-2.1-.7-1.6L3 13.7v-3.4l2.4-.5.7-1.6-1.4-2.1 2.4-2.4 2.1 1.4 1.6-.7z"/><circle class="line" cx="12" cy="12" r="3"/></svg>',
  shake: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect class="line" x="6.5" y="5.5" width="11" height="13" rx="1.5"/><path class="line" d="M2.8 9.5v5M21.2 9.5v5"/></svg>',
  lock: '<svg viewBox="0 0 12 14" aria-hidden="true"><rect class="fill" x="1" y="6" width="10" height="7.5" rx="1"/><path class="line" d="M3.4 6V4.3a2.6 2.6 0 0 1 5.2 0V6"/></svg>',
  /** Two laurel branches, curving up round a medal or behind the podium's winner. */
  laurel:
    '<svg viewBox="0 0 100 100" aria-hidden="true">' +
    [-1, 1]
      .map((s) =>
        Array.from({ length: 6 }, (_, i) => {
          // From the foot of the ring up each side, the leaves lying along it and getting smaller.
          const deg = 252 - i * 19;
          const a = (deg * Math.PI) / 180;
          const x = 50 - s * Math.cos(a) * 40;
          const y = 52 - Math.sin(a) * 40;
          return `<ellipse class="leaf" cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="${(8.5 - i * 0.5).toFixed(1)}" ry="3.4" transform="rotate(${s * (deg + 90)} ${x.toFixed(1)} ${y.toFixed(1)})"/>`;
        }).join(''),
      )
      .join('') +
    '</svg>',
};

export class Screens {
  private root: HTMLElement;
  private loadingEl: HTMLElement;
  private poster: HTMLElement;
  private field: HTMLElement;
  private views: Record<PosterView, HTMLElement>;
  private kicker: HTMLElement;
  private circuitEl: HTMLElement;
  private dayEl: HTMLElement;
  private closesEl: HTMLElement;
  private carName: HTMLElement;
  private carYear: HTMLElement;
  private tag: HTMLElement;
  private blurb: HTMLElement;
  private seals: HTMLElement;
  private progress: HTMLElement;
  private hintEl: HTMLElement;
  private artHost: HTMLElement;
  /** Days in a row: a rubber stamp on the picture's corner. */
  private streakEl: HTMLElement;
  /** The champion's day: sparkles round the gold car. */
  private sparkles: HTMLElement;
  private podiumBand: HTMLElement;
  private podiumCard: HTMLElement;
  private todayCard: HTMLElement;
  private chLabel: HTMLElement;
  private chPitch: HTMLElement;
  private chCircuitLabel: HTMLElement;
  private chOutline: HTMLElement;
  private chCircuit: HTMLElement;
  private chTag: HTMLElement;
  private chCarLabel: HTMLElement;
  private chCarPic: HTMLElement;
  private chCar: HTMLElement;
  private chBlurb: HTMLElement;
  private chTime: HTMLElement;
  private chWho: HTMLElement;
  private chBoard: HTMLElement;
  private chSteps: HTMLElement;
  private chTitle: HTMLElement;
  private chBeatLabel: HTMLElement;
  private chNote: HTMLElement;
  private chRaceAction: PosterActionId = 'race';
  private nudgeEl: HTMLElement;
  private chWatch: HTMLButtonElement;
  private chRace: HTMLButtonElement;
  private actionsEl: HTMLElement;
  private noticeEl: HTMLElement;
  private shareTimer = 0;
  /** For a moment after sharing, what happened ("link copied ✓"), on the challenge buttons. */
  private shareSaid: string | null = null;
  private info: TitleInfo | null = null;
  private pollRow: HTMLElement;
  /** The poll: not shown, asking, asking what's missing (after a no), or saying thanks. */
  private pollState: 'off' | 'ask' | 'why' | 'thanks' = 'off';
  private pollTimer = 0;
  private foot: HTMLElement;
  private smallPrint: HTMLElement;
  /** The line for a browser drawing without the graphics card (null: not shown). */
  private gpuHint: HTMLButtonElement | null = null;
  private motif: HTMLElement;
  private ticket: HTMLButtonElement;
  private ticketImg: HTMLImageElement;
  private ticketLabel: HTMLElement;
  private ticketSub: HTMLElement;
  private ticketKey: HTMLElement;
  private boardBtn: HTMLButtonElement;
  private lessonToggle: HTMLButtonElement;
  private soundBtn: HTMLButtonElement;
  private lineBtn: HTMLButtonElement;
  private pedalsBtn: HTMLButtonElement;
  private shakeBtn: HTMLButtonElement;
  private view: PosterView = 'main';
  private podiumKey = '';
  /** Today's leaderboard, the poster's third side. */
  readonly board: BoardPanel;
  readonly art = new PosterArt();
  onGo: () => void = () => {};
  /** One of the poster's buttons. */
  onAction: (id: PosterActionId) => void = () => {};
  /** Send your lap as a challenge (the share sheet on a phone, the clipboard elsewhere), from the poster or its card. */
  onChallenge: (from: 'poster' | 'card') => void = () => {};
  /** Watch one of yesterday's podium laps (by its place), the other two as ghosts. */
  onWatchPodium: (place: number) => void = () => {};
  /** "Read yesterday's paper": the morning paper again. */
  onPaper: () => void = () => {};
  /** Watch a lap from today's board (your next target, or your own). */
  onWatchEntry: (e: BoardEntry) => void = () => {};
  /** Inside a Discord server: its board, or everyone's. */
  onScope: (on: BoardScope['on']) => void = () => {};
  /** Your best wants a name to go on the board. */
  onSaveLap: () => void = () => {};
  onToday: () => void = () => {};
  /** The player turned the poster to a side (the board, how to play). */
  onOpened: (view: PosterView) => void = () => {};
  /** The poll was answered. */
  onPoll: (answer: PollAnswer) => void = () => {};
  /** Start the driving lesson. */
  onLesson: () => void = () => {};
  /** The driving line switch was tapped. */
  onLine: () => void = () => {};
  /** A phone's pedals switch was tapped: buttons or a slider. */
  onPedals: () => void = () => {};
  /** Send a player's note (the beta's "tell us" side): 'ok', or why not. */
  onFeedback: (text: string) => Promise<string> = async () => 'offline';
  private fbText!: HTMLTextAreaElement;
  private fbSend!: HTMLButtonElement;
  private fbMsg!: HTMLElement;
  private fbWith!: HTMLElement;
  private fbForm!: HTMLElement;
  private fbThanks!: HTMLElement;
  private fbBusy = false;
  /** The sound switch was tapped. */
  onSound: () => void = () => {};
  /** The screen shake switch was tapped. */
  onShake: () => void = () => {};
  /** Watch a friend who's driving now. */
  onWatchLive: (key: string) => void = () => {};
  /** The strip's "show them on my track" was tapped. */
  onFriends: () => void = () => {};
  /** "✕ race without their ghost" was tapped (racing a friend's lap). */
  onDropGhost: () => void = () => {};
  /** Inside Discord: invite friends (the line under race). */
  onInvite: () => void = () => {};
  private dropEl!: HTMLButtonElement;
  /** On the website: the Discord line under race was tapped (Grand Premio on Discord). */
  onDoor: () => void = () => {};
  /** On the website: the links to the privacy and terms pages, under how to play. */
  private legalEl!: HTMLElement;
  /** Friends driving now: the strip over the race button, and their rows on today's board. */
  private live: LiveDriver[] | null = null;
  private liveEl!: HTMLElement;
  private howLesson: HTMLButtonElement;
  /** The card over the pause menu asking a player with a made-up name for their own, while it's up. */
  private askEl: HTMLElement | null = null;
  private popupEl: HTMLElement | null = null;
  /** Under race: the Discord ticket (the website, after a first lap) or the invite (inside Discord). */
  private dcEl: HTMLElement;
  private dcKey = '';
  /** The quick mute beside settings (the switch itself is in settings). */
  private muteBtn: HTMLButtonElement;
  private legalSettings: HTMLElement;
  private stampEl: HTMLElement | null = null;
  private popupPress: ((yes: boolean) => void) | null = null;

  constructor(parent: HTMLElement) {
    this.root = el('div', 'screen interactive', parent);
    this.loadingEl = el('div', 'loading', this.root, 'drawing the circuit…');
    // Beside the sheet on wide screens: yesterday's podium (left) and today's board (right).
    this.podiumCard = el('aside', 'side-card podium podium-card', this.root);
    this.poster = el('div', 'poster', this.root);
    this.todayCard = el('aside', 'side-card today-card', this.root);
    this.poster.style.display = 'none';
    this.field = el('div', 'field', this.poster);

    // --- side 1: the day's race
    const main = el('section', 'view main', this.field);
    // The masthead, the podium (narrow screens) and the illustration; beside or below them, the car and how you're doing.
    const lead = el('div', 'lead', main);
    const head = el('header', 'head', lead);
    this.kicker = el('div', 'kicker', head);
    el('h1', '', head, 'grand premio');
    this.circuitEl = el('h2', '', head);
    const when = el('div', 'when', head);
    this.dayEl = el('span', 'day', when);
    this.closesEl = el('span', 'closes', when);
    this.podiumBand = el('div', 'podium podium-band', lead);
    const art = el('div', 'art', lead);
    this.artHost = art;
    // The day's motif behind the car.
    this.motif = el('div', 'motif', art);
    art.appendChild(this.art.canvas);
    // The picture's box can change without the window changing (friends on track move yesterday's podium aside, a
    // note comes and goes): drawn again at its new size, never stretched.
    if (typeof ResizeObserver !== 'undefined') {
      let drawn = '';
      new ResizeObserver(() => {
        const c = this.art.canvas;
        const size = `${c.clientWidth}x${c.clientHeight}`;
        if (size === drawn || !c.clientWidth || !c.clientHeight) return;
        drawn = size;
        requestAnimationFrame(() => this.art.render());
      }).observe(this.art.canvas);
    }
    // The poll (once, after a few laps): a paper slip clipped to the picture's corner.
    this.pollRow = el('div', 'poll', art);
    this.pollRow.hidden = true;
    this.streakEl = el('div', 'streak', art);
    this.streakEl.hidden = true;
    this.sparkles = el('div', 'sparkles', art);
    this.sparkles.setAttribute('aria-hidden', 'true');
    // Four-point stars in cream, round the car.
    for (const [x, y, s, r] of [[26, 18, 2.2, 0], [70, 24, 1.6, 20], [73, 63, 2.4, -8], [55, 8, 1.2, 30]]) {
      const star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      star.setAttribute('viewBox', '0 0 20 20');
      star.style.cssText = `left:${x}%;top:${y}%;width:${s}em;height:${s}em;transform:rotate(${r}deg)`;
      star.innerHTML = '<path d="M10 0 C11 7 13 9 20 10 C13 11 11 13 10 20 C9 13 7 11 0 10 C7 9 9 7 10 0Z"/>';
      this.sparkles.appendChild(star);
    }
    this.sparkles.hidden = true;
    const info = el('div', 'info', main);
    const car = el('div', 'car', info);
    this.carName = el('b', '', car);
    this.carYear = el('span', 'year', car);
    this.tag = el('span', 'tag', car);
    this.blurb = el('div', 'blurb', info);
    this.hintEl = el('div', 'challenge-hint', info);
    // Racing a friend's lap: the way to race on your own instead.
    this.dropEl = el('button', 'link drop-ghost', info) as HTMLButtonElement;
    this.dropEl.type = 'button';
    this.dropEl.onclick = () => this.onDropGhost();
    this.dropEl.hidden = true;
    this.seals = el('div', 'seals', info);
    this.progress = el('div', 'progress', info);
    this.noticeEl = el('div', 'notice', info);
    this.nudgeEl = el('div', 'nudge', info);

    // --- side 2: how it works
    const how = el('section', 'view how', this.field);
    const hh = el('header', 'head', how);
    el('div', 'kicker', hh, 'grand premio');
    el('h1', '', hh, 'how to play');
    const steps = el('ol', 'steps', how);
    for (const [title, text] of [
      ['one circuit a day', 'Every day at midnight (Rome time) brings a new circuit and a new car. Everyone races the same one.'],
      ['your fastest lap', 'Lap as often as you like: only your fastest clean lap counts. Keep at least one wheel on the track.'],
      ['the leaderboard', 'Beat the bronze, silver and gold times, save your lap under your name and see where you stand. The board closes at midnight.'],
      ['challenge your friends', 'Tap “challenge your friends” and send the link: they watch your lap, then race it with your car as a ghost. On the board you can watch the driver just ahead of you.'],
    ]) {
      const li = el('li', '', steps);
      el('h3', '', li, title);
      el('p', '', li, text);
    }
    const howActions = el('div', 'how-actions', how);
    const ok = el('button', 'go', howActions, 'got it');
    ok.type = 'button';
    ok.onclick = () => this.show('main');
    this.howLesson = el('button', 'second', howActions, 'driving lesson ▸');
    this.howLesson.type = 'button';
    this.howLesson.onclick = () => this.onLesson();
    const tell = el('button', 'link tell', how, 'found a bug? tell us ▸');
    tell.type = 'button';
    tell.onclick = () => this.open('feedback');
    // The website's privacy and terms pages (not inside Discord: a page there would replace the game).
    this.legalEl = el('div', 'legal', how);
    for (const [i, [href, words]] of [['privacy', 'privacy'], ['terms', 'terms']].entries()) {
      if (i) this.legalEl.append(' · ');
      const a = el('a', '', this.legalEl, words) as HTMLAnchorElement;
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener';
    }

    // --- side 4: a friend's challenge, set out like a race programme
    const chView = el('section', 'view challenge-view', this.field);
    const chHead = el('header', 'head', chView);
    this.chLabel = el('div', 'kicker', chHead);
    this.chTitle = el('h1', '', chHead, 'grand premio');
    this.chPitch = el('div', 'pitch', chHead);
    const facts = el('div', 'facts', chView);
    const circuitBox = el('div', 'fact', facts);
    this.chCircuitLabel = el('div', 'label', circuitBox);
    this.chOutline = el('div', 'outline', circuitBox);
    this.chCircuit = el('b', '', circuitBox);
    this.chTag = el('span', 'tag', circuitBox);
    const carBox = el('div', 'fact', facts);
    this.chCarLabel = el('div', 'label', carBox);
    this.chCarPic = el('div', 'pic', carBox);
    this.chCar = el('b', '', carBox);
    this.chBlurb = el('span', 'blurb', carBox);
    const plan = el('div', 'plan', chView);
    const stakes = el('div', 'stakes', plan);
    const beat = el('div', 'beat', stakes);
    this.chBeatLabel = el('div', 'label', beat, 'time to beat');
    this.chTime = el('b', 'time', beat);
    this.chWho = el('span', 'who', beat);
    this.chBoard = el('div', 'mini', stakes);
    this.chSteps = el('ol', 'steps', plan);
    this.chNote = el('p', 'note', plan);
    const chActions = el('div', 'actions', plan);
    this.chWatch = el('button', 'go', chActions);
    this.chWatch.type = 'button';
    this.chWatch.onclick = () => this.onAction('watch');
    this.chRace = el('button', 'second', chActions);
    this.chRace.type = 'button';
    this.chRace.onclick = () => this.onAction(this.chRaceAction);

    // --- side 6: settings: the switches, each with what it does, the lesson, Discord, privacy and terms.
    const settings = el('section', 'view settings', this.field);
    const sh = el('header', 'head', settings);
    el('div', 'kicker', sh, 'grand premio');
    el('h1', '', sh, 'settings');
    const list = el('div', 'setting-list', settings);
    const setting = (kind: string, onClick: () => void, say: string): HTMLButtonElement => {
      const r = el('div', `setting ${kind}`, list);
      const b = this.toggle(r, kind, onClick);
      el('p', 'say', r, say);
      return b;
    };
    this.soundBtn = setting('sound', () => this.onSound(), 'The engine, the tyres and the countdown. M on a keyboard.');
    // Phones only: the gas and brake as buttons or a slider, named as it is now (a tap switches).
    this.pedalsBtn = setting('pedals', () => this.onPedals(), 'Gas and brake as two buttons, or one slider under your thumb.');
    this.shakeBtn = setting('shake', () => this.onShake(), 'The camera trembles a little at speed, on the grass and against a wall.');
    this.lineBtn = setting('line', () => this.onLine(), 'The racing line and the braking zones, painted on the road.');
    this.lessonToggle = setting('lesson', () => this.onLesson(), 'Scuola guida: a minute with the instructor, turn 1 and a whole lap.');
    this.lessonToggle.querySelector('.name')!.textContent = 'driving lesson';
    this.lessonToggle.setAttribute('aria-label', 'scuola guida: the 1-minute driving lesson');
    const done = el('button', 'go', el('div', 'how-actions', settings), 'done');
    done.type = 'button';
    done.onclick = () => this.show('main');
    this.legalSettings = el('div', 'legal', settings);
    for (const [i, href] of ['privacy', 'terms'].entries()) {
      if (i) this.legalSettings.append(' · ');
      const a = el('a', '', this.legalSettings, href) as HTMLAnchorElement;
      a.href = href;
      a.target = '_blank';
      a.rel = 'noopener';
    }

    // --- side 3: today's board
    const boardView = el('section', 'view board-view', this.field);
    this.board = new BoardPanel(boardView);
    this.board.onBack = () => this.show('main');

    // --- side 5: the beta's "tell us"
    const fbView = el('section', 'view feedback-view', this.field);
    this.buildFeedback(fbView);

    this.views = { main, how, board: boardView, challenge: chView, feedback: fbView, settings };

    // The beta: an overprinted strip across the sheet's corner, the way to "tell us".
    const corner = el('div', 'beta-corner', this.poster);
    const sash = el('button', 'beta-sash', corner);
    sash.type = 'button';
    el('b', '', sash, 'beta');
    el('span', '', sash, 'feedback ▸');
    sash.setAttribute('aria-label', 'Grand Premio is in beta: send feedback');
    sash.onclick = () => this.open('feedback');

    // The cream strip along the sheet's foot: race and the lesson, the switches, and for newcomers the controls.
    this.foot = el('div', 'foot', this.poster);
    this.liveEl = el('div', 'live-strip', this.foot);
    this.liveEl.hidden = true;
    const row = el('div', 'act-row', this.foot);
    this.actionsEl = el('div', 'actions', row);
    this.boardBtn = el('button', 'second board', row, 'leaderboard');
    this.boardBtn.type = 'button';
    this.boardBtn.onclick = () => this.open('board');
    this.ticket = el('button', 'ticket', row);
    this.ticket.type = 'button';
    this.ticket.onclick = () => this.onLesson();
    this.ticketImg = el('img', 'helmet', this.ticket);
    this.ticketImg.alt = '';
    const words = el('span', 'words', this.ticket);
    this.ticketLabel = el('span', 'label', words);
    el('b', '', words, 'scuola guida');
    this.ticketSub = el('span', 'sub', words, '1-minute lesson ▸');
    this.ticketKey = el('span', 'key', this.ticket);
    // Discord, under race: on the website (after a first lap) the ticket that explains it, inside Discord the invite.
    this.dcEl = el('div', 'dc-ticket', this.foot);
    this.dcEl.hidden = true;
    // Then the quiet links: sound in one tap, the settings, how to play.
    const links = el('div', 'foot-links', this.foot);
    this.muteBtn = el('button', 'mute', links) as HTMLButtonElement;
    this.muteBtn.type = 'button';
    this.muteBtn.onclick = () => this.onSound();
    const setBtn = el('button', 'foot-link settings-link', links) as HTMLButtonElement;
    setBtn.type = 'button';
    setBtn.innerHTML = `<span class="glyph" aria-hidden="true">${GLYPH.gear}</span>settings`;
    setBtn.onclick = () => this.open('settings');
    const howBtn = el('button', 'foot-link how-link', links) as HTMLButtonElement;
    howBtn.type = 'button';
    howBtn.innerHTML = '<span class="glyph" aria-hidden="true">?</span>how to play';
    howBtn.onclick = () => this.open('how');
    this.smallPrint = el('div', 'small-print', this.foot);

    // A tap on the scene around the poster starts the race; on the poster and its cards (and the name card
    // over it), only the buttons do things.
    this.root.addEventListener('pointerdown', (e) => {
      if (this.poster.style.display === 'none') return;
      if (!(e.target as HTMLElement).closest('.poster, .side-card, .name-ask, .discord-pop')) this.onGo();
    });
    this.show('main');
  }

  /** The "tell us" side: a few words, what they go with, send. */
  private buildFeedback(view: HTMLElement): void {
    const head = el('header', 'head', view);
    el('div', 'kicker', head, 'grand premio · beta');
    el('h1', '', head, 'tell us');
    this.fbForm = el('div', 'fb-form', view);
    el('p', 'pitch', this.fbForm, 'What broke, what was confusing, what you’d love to see. A sentence is plenty.');
    this.fbText = el('textarea', 'fb-text', this.fbForm);
    this.fbText.maxLength = FEEDBACK_MAX;
    this.fbText.rows = 5;
    this.fbText.placeholder = 'e.g. the brake is hard to reach on my phone';
    this.fbText.setAttribute('aria-label', 'Your feedback');
    this.fbText.oninput = () => (this.fbSend.disabled = this.fbBusy || this.fbText.value.trim() === '');
    this.fbWith = el('p', 'fb-with', this.fbForm);
    const actions = el('div', 'fb-actions', this.fbForm);
    this.fbSend = el('button', 'go', actions, 'send');
    this.fbSend.type = 'button';
    this.fbSend.disabled = true;
    this.fbSend.onclick = () => void this.sendNote();
    const back = el('button', 'link', actions, '◂ back');
    back.type = 'button';
    back.onclick = () => this.show('main');
    this.fbMsg = el('p', 'fb-msg', this.fbForm);
    this.fbThanks = el('div', 'fb-thanks', view);
    this.fbThanks.hidden = true;
    el('p', 'big', this.fbThanks, 'thank you!');
    el('p', 'pitch', this.fbThanks, 'We read every note. Back to the race?');
    const again = el('button', 'go', this.fbThanks, 'back ▸');
    again.type = 'button';
    again.onclick = () => this.show('main');
  }

  /** What a note goes out with, said plainly under the box. */
  setFeedbackContext(device: string): void {
    this.fbWith.textContent = `sent with: ${device} · nothing else about you`;
  }

  private async sendNote(): Promise<void> {
    const text = this.fbText.value.trim();
    if (!text || this.fbBusy) return;
    this.fbBusy = true;
    this.fbSend.disabled = true;
    this.fbSend.textContent = '…';
    this.fbMsg.textContent = '';
    const r = await this.onFeedback(text);
    this.fbBusy = false;
    this.fbSend.textContent = 'send';
    if (r === 'ok') {
      this.fbText.value = '';
      this.fbForm.hidden = true;
      this.fbThanks.hidden = false;
      return;
    }
    this.fbSend.disabled = false;
    this.fbMsg.textContent = r === 'offline' ? 'couldn’t send · you seem to be offline' : r === 'slow down' ? 'too many at once · try again in a minute' : `couldn’t send · ${r}`;
  }

  /** A switch in the foot: a printed glyph and its name (struck through when off). */
  private toggle(parent: HTMLElement, kind: string, onClick: () => void): HTMLButtonElement {
    const b = el('button', `toggle ${kind}`, parent);
    b.type = 'button';
    el('span', 'glyph', b);
    el('span', 'name', b);
    b.onclick = onClick;
    return b;
  }

  show(view: PosterView): void {
    // Back on "tell us": a fresh form (after a thank-you).
    if (view === 'feedback' && this.view !== 'feedback') {
      this.fbForm.hidden = false;
      this.fbThanks.hidden = true;
      this.fbMsg.textContent = '';
    }
    const opening = view !== this.view;
    this.view = view;
    for (const [k, e] of Object.entries(this.views)) e.classList.toggle('on', k === view);
    if (opening && view === 'board') this.board.reveal();
    this.poster.dataset.view = view;
    this.root.dataset.view = view;
    // The day's car picture: on the main side, or in the challenge's "your car" box.
    const host = view === 'challenge' ? this.chCarPic : this.artHost;
    if (this.art.canvas.parentElement !== host) host.insertBefore(this.art.canvas, host === this.artHost ? this.pollRow : null);
    this.posePicture();
    if (view === 'main' || view === 'challenge') requestAnimationFrame(() => this.art.render());
  }

  /** A side the player asked for. */
  private open(view: PosterView): void {
    this.show(view);
    this.onOpened(view);
  }

  get currentView(): PosterView {
    return this.view;
  }

  /** Days in a row (shown from the second): a red rubber stamp on the picture's corner. Null hides it. */
  setStreak(days: number | null): void {
    const on = days !== null && days >= 2;
    this.streakEl.hidden = !on;
    if (!on) return;
    this.streakEl.replaceChildren();
    el('b', '', this.streakEl, String(days));
    const words = el('span', '', this.streakEl, 'days in');
    words.append(document.createElement('br'), 'a row');
  }

  /** The champion's day: sparkles round their gold car. */
  setGold(on: boolean): void {
    this.sparkles.hidden = !on;
  }

  openBoard(): void {
    this.show('board');
  }

  /** Between circuits: the page's loading poster covers everything (see boot.ts). */
  loading(text?: string): void {
    this.root.classList.remove('hidden', 'gone');
    this.loadingEl.style.display = 'none';
    this.poster.style.display = 'none';
    this.root.classList.remove('cards');
    bootLoading(text);
  }

  showTitle(o: TitleInfo): void {
    this.info = o;
    this.applyStyle(o.style);
    this.root.classList.remove('hidden', 'gone');
    this.loadingEl.style.display = 'none';
    this.poster.style.display = '';
    bootDone();
    this.poster.classList.toggle('paused', o.paused);
    const ch = o.challenge ?? null;
    this.poster.classList.toggle('challenge', !!ch);
    this.kicker.textContent = ch ? ch.kicker : o.paused ? 'paused' : o.car.preview ? 'preview' : 'today’s fastest lap';
    this.circuitEl.textContent = o.circuit.toLowerCase();
    this.dayEl.textContent = o.day;
    this.closesEl.textContent = o.closes ?? '';
    this.closesEl.hidden = !o.closes;
    this.carName.textContent = o.car.name.toLowerCase();
    this.carYear.textContent = String(o.car.year);
    const tag = circuitTag(o.signature, o.crossover, o.terrain);
    this.tag.textContent = tag;
    this.tag.hidden = !tag;
    // Racing someone's lap: their time takes the blurb's place, and what happens next is spelled out.
    this.blurb.textContent = ch ? ch.line : o.car.blurb;
    this.blurb.classList.toggle('challenge-line', !!ch);
    this.hintEl.textContent = ch?.hint ?? '';
    this.hintEl.hidden = !ch?.hint;
    this.dropEl.textContent = ch?.drop ?? '';
    this.dropEl.hidden = !ch?.drop;
    if (ch?.intro) this.renderIntro(ch.intro);
    this.renderSeals(o);
    this.renderProgress();
    this.renderActions(o);
    this.renderSecond(o);
    this.noticeEl.textContent = o.notice ?? '';
    this.noticeEl.hidden = !o.notice;
    this.nudgeEl.textContent = o.nudge ?? '';
    this.nudgeEl.hidden = !o.nudge;
    this.setPoll(!!o.poll);
    // A controller reaches the lesson with Y (links need a pointer): say so.
    this.howLesson.textContent = `driving lesson ▸${o.controls === 'pad' ? ' · Y' : ''}`;
    this.setToggle(this.shakeBtn, 'screen shake', o.shake !== false, GLYPH.shake);
    this.renderDiscord(o);
    this.shakeBtn.setAttribute('aria-label', o.shake !== false ? 'Screen shake on: switch it off' : 'Screen shake off: switch it on');
    this.lineBtn.hidden = o.line == null;
    const slider = o.pedals === 'slider';
    this.pedalsBtn.hidden = o.controls !== 'touch';
    this.setToggle(this.pedalsBtn, slider ? 'pedal slider' : 'pedal buttons', true, slider ? GLYPH.pedalSlider : GLYPH.pedalButtons);
    this.pedalsBtn.setAttribute('aria-label', slider ? 'Gas and brake on a slider: switch to buttons' : 'Gas and brake on buttons: switch to a slider');
    this.setToggle(this.lineBtn, 'line', !!o.line, GLYPH.line);
    // Yesterday's podium and today's board: in the sheet, or beside it where there's room (main.css decides).
    // Paused is the same menu, with "resume".
    const podium = !ch ? (o.podium ?? null) : null;
    this.renderPodium(podium, o.controls);
    this.renderToday(!ch ? (o.today ?? null) : null);
    this.root.classList.toggle('cards', !ch && (!!podium || !!o.today));
    // The controls, for someone who's never finished a lap.
    this.smallPrint.hidden = !o.firstTime;
    this.smallPrint.textContent =
      o.controls === 'pad'
        ? 'A race · left stick steers · RT gas · LT brake · Y restart · Start pause · B back'
        : o.controls === 'touch'
          ? o.pedals === 'slider'
            ? 'slide a thumb on the left to steer · on the right, thumb down for gas, slide down to ease off and brake'
            : 'slide a thumb on the left to steer · gas and brake on the right'
          : 'any key to start · WASD or arrows · R restart · Esc pause';
    requestAnimationFrame(() => this.art.render());
  }

  /** The three medal times as medals: printed in colour once won, only their outline until then. */
  private renderSeals(o: TitleInfo): void {
    this.seals.replaceChildren();
    for (const m of ['bronze', 'silver', 'gold'] as const) {
      const won = o.won[m];
      const s = el('div', `seal ${m}${won ? ' won' : ''}`, this.seals);
      s.innerHTML = MEDAL;
      const txt = el('span', 'txt', s);
      el('span', 'm', txt, m);
      el('b', '', txt, formatLap(o.medals[m]));
      s.setAttribute('aria-label', `${m} ${formatLap(o.medals[m])}${won ? ', won' : ''}`);
    }
  }

  /**
   * One line under the medals: your best, where it stands (a way to the board) and a way to share it;
   * before your first lap, how to get on the board.
   */
  private renderProgress(): void {
    const o = this.info;
    const row = this.progress;
    row.replaceChildren();
    row.classList.remove('done');
    if (!o) return;
    const parts: HTMLElement[] = [];
    const link = (label: string, onClick: () => void): HTMLButtonElement => {
      const b = el('button', 'link', undefined, label);
      b.type = 'button';
      b.onclick = onClick;
      return b;
    };
    row.classList.toggle('ticketed', o.bestMs !== null && !!o.share);
    if (o.bestMs !== null && o.share) {
      // Your lap as a ticket: the time and your place on the stub, the challenge on the other half.
      const t = el('div', 'lap-ticket', row);
      const stub = el('div', 'stub', t);
      el('span', 'lbl', stub, 'your lap');
      el('b', '', stub, formatLap(o.bestMs));
      if (o.standing) stub.appendChild(link(`${ordinal(o.standing.rank)} today · leaderboard ▸`, () => this.open('board')));
      else if (o.saveable) stub.appendChild(link('save it to the leaderboard ▸', () => this.onSaveLap()));
      const c = el('button', 'challenge', t, this.shareSaid ?? 'challenge your friends ↗\uFE0E');
      c.type = 'button';
      c.classList.toggle('said', !!this.shareSaid);
      c.onclick = () => this.onChallenge('poster');
      row.hidden = false;
      return;
    } else if (o.bestMs !== null) {
      const mine = el('span', 'mine');
      mine.append('best ');
      el('b', '', mine, formatLap(o.bestMs));
      parts.push(mine);
      if (o.standing) parts.push(link(`${ordinal(o.standing.rank)} · leaderboard ▸`, () => this.open('board')));
      else if (o.saveable) parts.push(link('save it to the leaderboard ▸', () => this.onSaveLap()));
    } else if (o.drivers != null) {
      // Today's board is open: how to get on it, and the way in.
      const join = el('span', 'join');
      join.append('set a lap to get on the ');
      join.appendChild(link('leaderboard ▸', () => this.open('board')));
      parts.push(join);
    }
    if (o.otherDay) parts.push(link('today’s circuit ▸', () => this.onToday()));
    parts.forEach((p, i) => {
      if (i > 0) el('span', 'dot', row, '·');
      row.appendChild(p);
    });
    row.hidden = parts.length === 0;
  }


  /** The buttons along the foot: race (or resume), or a challenge's two. */
  private renderActions(o: TitleInfo): void {
    this.actionsEl.replaceChildren();
    o.actions.forEach((a, i) => {
      const b = el('button', i === 0 ? 'go' : 'second', this.actionsEl, a.label);
      b.type = 'button';
      b.dataset.action = a.id;
      // A controller's A presses the big one.
      if (i === 0 && o.controls === 'pad') el('span', 'key', b, 'A');
      b.onclick = () => (a.id === 'board' ? this.open('board') : this.onAction(a.id));
    });
    this.actionsEl.dataset.count = String(o.actions.length);
  }

  /**
   * The place beside race: the lesson's ticket while it's being offered (new here, or restarting a lot),
   * then "leaderboard", with the lesson moved to the switches. A challenge's two buttons take the whole row.
   */
  private renderSecond(o: TitleInfo): void {
    const kind = o.lesson ?? null;
    const single = o.actions.length === 1;
    const board = single && !!o.boardButton && kind !== 'new' && kind !== 'nudge';
    this.boardBtn.hidden = !board;
    this.ticket.hidden = board || kind === null || !single;
    this.lessonToggle.hidden = kind === null;
    if (kind === null) return;
    this.ticket.className = `ticket ${kind}`;
    if (!this.ticketImg.src) {
      // The helmet is drawn once, a frame after the poster first shows.
      requestAnimationFrame(() => {
        if (this.ticketImg.src) return;
        const helmet = helmetPicture() || 'data:,';
        this.ticketImg.src = helmet;
        const img = el('img', '', this.lessonToggle.querySelector('.glyph') as HTMLElement);
        img.src = helmet;
        img.alt = '';
      });
    }
    this.ticketLabel.textContent = kind === 'new' ? 'new here?' : kind === 'nudge' ? 'trouble with the corners?' : '';
    this.ticketLabel.hidden = kind !== 'new' && kind !== 'nudge';
    // Once you've won a medal, just the helmet and a word.
    this.ticketSub.textContent = kind === 'small' ? 'lesson ▸' : '1-minute lesson ▸';
    // The key that starts it from the poster: R on a keyboard, Y on a controller.
    this.ticketKey.textContent = o.controls === 'pad' ? 'Y' : o.controls === 'keys' ? 'R' : '';
    this.ticketKey.hidden = o.controls === 'touch';
    this.ticket.setAttribute('aria-label', 'scuola guida: the 1-minute driving lesson');
  }

  /**
   * Back from Discord's sign-in, begun somewhere else (another browser, or someone's link): "sign in as Camilo?"
   * first. Nothing changes until it's answered yes.
   */
  askSignIn(o: { name: string; onYes: () => void; onNo: () => void }): void {
    this.closePopup();
    const back = el('div', 'discord-pop ask interactive', this.root);
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', `Sign in with Discord as ${o.name}?`);
    const card = el('div', 'card', back);
    const title = el('h2', '', card);
    title.insertAdjacentHTML('beforeend', `<span class="discord-mark">${GLYPH.discord}</span>`);
    title.append('sign in as ');
    el('b', 'who', title, `${o.name}?`);
    el('p', 'line', card, 'This device’s laps join your Discord player: the same on every device, and inside Discord.');
    const yes = el('button', 'go with-mark', card) as HTMLButtonElement;
    yes.type = 'button';
    yes.innerHTML = `<span class="discord-mark">${GLYPH.discord}</span>sign in`;
    const no = el('button', 'later', card, 'that’s not me') as HTMLButtonElement;
    no.type = 'button';
    const answer = (f: () => void) => (): void => {
      this.closePopup();
      f();
    };
    yes.onclick = answer(o.onYes);
    no.onclick = answer(o.onNo);
    this.popupPress = (y) => (y ? answer(o.onYes) : answer(o.onNo))();
    this.popupEl = back;
    yes.focus({ preventScroll: true });
  }

  private setToggle(b: HTMLButtonElement, name: string, on: boolean, glyph: string): void {
    b.classList.toggle('off', !on);
    b.querySelector('.glyph')!.innerHTML = glyph;
    b.querySelector('.name')!.textContent = name;
    b.setAttribute('aria-pressed', String(on));
    b.setAttribute('aria-label', `${name === 'line' ? 'Racing line and braking zones' : 'Sound'}: ${on ? 'on' : 'off'}`);
    b.title = name === 'line' ? `The racing line and braking zones on the road: ${on ? 'on' : 'off'}` : `Sound: ${on ? 'on' : 'off'} (M)`;
  }

  /** Yesterday's podium, in the sheet (a band) and beside it (a card); main.css shows the one that fits. */
  private renderPodium(p: PodiumInfo | null, controls: TitleInfo['controls']): void {
    this.podiumBand.hidden = !p;
    this.podiumCard.hidden = !p;
    if (!p) return;
    const key = JSON.stringify([p, controls]);
    if (key === this.podiumKey) return;
    this.podiumKey = key;
    const pictures = figurePictures(podiumFigures(p.places.map((x) => x.name)));
    for (const host of [this.podiumBand, this.podiumCard]) {
      host.replaceChildren();
      const head = el('div', 'podium-head', host);
      el('h3', '', head, 'yesterday’s podium');
      // Where it was: the circuit, then the car (a line each).
      const where = el('div', 'where', head);
      for (const part of p.where.split(' · ')) el('span', '', where, part);
      const steps = el('div', 'steps', host);
      // Second on the left, the winner in the middle, third on the right.
      for (const place of [2, 1, 3]) {
        const i = p.places.findIndex((x) => x.place === place);
        const who = i >= 0 ? p.places[i] : null;
        const step = el(who ? 'button' : 'div', `step p${place}${who ? '' : ' empty'}`, steps);
        if (place === 1) step.insertAdjacentHTML('afterbegin', `<span class="laurel">${GLYPH.laurel}</span>`);
        const fig = el('span', 'fig', step);
        if (who && pictures[i]) {
          const img = el('img', '', fig);
          img.src = pictures[i];
          img.alt = '';
          img.draggable = false;
        } else if (!who) el('span', 'nobody', fig, '?');
        el('span', 'block', step, String(place));
        const plaque = el('span', 'plaque', step);
        el('span', 'name', plaque, who ? who.name : '—');
        if (who) el('span', 'time', plaque, `${formatLap(who.ms)} ▸`);
        if (who && step instanceof HTMLButtonElement) {
          step.type = 'button';
          step.setAttribute('aria-label', `Watch ${who.name}’s lap: ${ordinal(place)} yesterday, ${formatLap(who.ms)}`);
          // A flag pops up over the figure under the pointer.
          el('span', 'flag', step, 'watch ▸');
          step.onclick = () => this.onWatchPodium(place);
        }
      }
      if (host === this.podiumCard) {
        const all = el('button', 'link all', host, '▸ watch the three race');
        all.type = 'button';
        all.onclick = () => this.onWatchPodium(1);
        if (p.places.length < 2) all.textContent = `▸ watch ${p.places[0]?.name ?? 'the'} lap`;
        if (p.paper) this.paperLink(host, '▸ read yesterday’s paper');
      } else {
        el('div', 'tip', head, controls === 'touch' ? 'tap a driver to watch' : 'pick a driver to watch their lap');
        if (p.paper) this.paperLink(head, 'read the paper ▸');
        host.insertAdjacentHTML('beforeend', SLANT);
      }
    }
  }

  /** The morning paper again, from yesterday's podium. */
  private paperLink(host: HTMLElement, text: string): void {
    const b = el('button', 'link paper-again', host, text);
    b.type = 'button';
    b.onclick = () => this.onPaper();
  }

  /** The privacy and terms links under how to play: on the website, not inside Discord. */
  setLegal(on: boolean): void {
    this.legalEl.hidden = !on;
    this.legalSettings.hidden = !on;
  }

  /**
   * Under race, Discord: on the website after a first lap, one line to the Discord card (the two screens, adding it,
   * signing in); once signed in, the name on it. Inside Discord, the invite.
   */
  private renderDiscord(o: TitleInfo): void {
    const as = o.signIn?.as ?? null;
    const kind = o.invite ? 'invite' : !o.discordDoor ? '' : as !== null ? 'in' : 'out';
    this.dcEl.hidden = kind === '';
    const key = `${kind}|${as}`;
    if (key === this.dcKey) return;
    this.dcKey = key;
    const d = this.dcEl;
    d.replaceChildren();
    d.className = `dc-ticket ${kind}`;
    if (kind === '') return;
    const b = el('button', 'line-btn', d) as HTMLButtonElement;
    b.type = 'button';
    b.insertAdjacentHTML('afterbegin', `<span class="discord-mark">${GLYPH.discord}</span>`);
    if (kind === 'invite') {
      el('b', '', b, 'race with friends');
      el('span', 'go-on', b, 'invite them ▸');
      b.onclick = () => this.onInvite();
      return;
    }
    el('b', '', b, kind === 'in' ? as! : 'race together on discord');
    el('span', 'go-on', b, kind === 'in' ? 'race together on discord ▸' : 'your friends’ cars on your track, live ▸');
    b.onclick = () => this.onDoor();
  }


  /**
   * Friends driving right now (inside a Discord server): over the race button, and marked on today's board. Under
   * them, whether their cars show on your track as you race (`friends`). Nobody: no strip at all.
   */
  setLive(drivers: LiveDriver[] | null, friends = true): void {
    this.live = drivers?.length ? drivers : null;
    const box = this.liveEl;
    box.replaceChildren();
    box.hidden = !this.live;
    const o = this.info;
    if (o) this.renderToday(!o.challenge ? (o.today ?? null) : null);
    if (!this.live) return;
    const lead = el('div', 'lead', box);
    el('i', 'dot', lead);
    el('span', '', lead, 'on track now');
    const list = el('div', 'drivers', box);
    for (const d of this.live) {
      const b = el('button', `driver${d.paused ? ' paused' : ''}`, list);
      b.type = 'button';
      el('b', '', b, d.name);
      el('span', '', b, d.status);
      b.setAttribute('aria-label', `Watch ${d.name} live`);
      b.onclick = () => this.onWatchLive(d.key);
    }
    const first = this.live.find((d) => !d.paused) ?? this.live[0];
    const go = el('button', 'watch-live', box, 'watch ▸');
    go.type = 'button';
    go.setAttribute('aria-label', `Watch ${first.name} live`);
    go.onclick = () => this.onWatchLive(first.key);
    const mine = el('button', `mine${friends ? ' on' : ''}`, box, `${friends ? '☑' : '☐'} show them on my track`);
    mine.type = 'button';
    mine.setAttribute('aria-pressed', String(friends));
    mine.title = friends ? 'As you race, the friends near you drive on your track as ghosts (they see you either way)' : 'Friends’ cars are hidden as you race (they still see yours)';
    mine.onclick = () => this.onFriends();
    // Inviting friends, here while friends are on track (the line under race steps aside: the car keeps its room).
    const invite = el('button', 'invite', box) as HTMLButtonElement;
    invite.type = 'button';
    invite.innerHTML = `<span class="discord-mark">${GLYPH.discord}</span>invite friends ▸`;
    invite.onclick = () => this.onInvite();
  }

  /** Today's board at a glance: the top three, your next target, you, and the way to the whole board. */
  private renderToday(t: TodayInfo | null): void {
    const c = this.todayCard;
    c.hidden = !t;
    if (!t) return;
    c.replaceChildren();
    const head = el('div', 'today-head', c);
    el('h3', '', head, 'today so far');
    el('span', 'count', head, `${t.total} ${t.total === 1 ? 'driver' : 'drivers'}`);
    if (t.scope) scopeSwitch(c, t.scope, (on) => this.onScope(on));
    const rows = el('div', 'rows', c);
    const row = (e: BoardEntry, cls = ''): void => {
      const live = e.you ? undefined : this.live?.find((d) => d.name === e.name);
      const r = el('div', `row${e.you ? ' you' : ''}${live ? ' live' : ''}${cls}`, rows);
      el('span', 'rank', r, String(e.rank));
      const name = el('span', 'name', r);
      if (live) el('i', 'dot', name);
      name.append(e.you ? 'you' : e.name);
      el('span', 'time', r, formatLap(e.lapMs));
      if (live) {
        const w = el('button', 'watch live', r, 'live ▸');
        w.type = 'button';
        w.setAttribute('aria-label', `Watch ${e.name} live: driving now`);
        w.onclick = () => this.onWatchLive(live.key);
      } else if (e.ref && !e.you) {
        const w = el('button', 'watch', r, 'watch ▸');
        w.type = 'button';
        w.setAttribute('aria-label', `Watch ${e.name}’s lap`);
        w.onclick = () => this.onWatchEntry(e);
      } else if (!e.you) {
        const lock = el('span', 'lock', r);
        lock.innerHTML = GLYPH.lock;
        lock.title = e.rank === 1 ? 'The leader’s lap stays secret till tomorrow' : 'Ahead of you: only your next target can be watched';
      }
    };
    for (const e of t.top) row(e);
    const shown = new Set(t.top.map((e) => e.rank));
    if (t.target && !shown.has(t.target.rank)) {
      el('div', 'label', rows, 'your next target');
      row(t.target, ' target');
      shown.add(t.target.rank);
    } else if (t.target) rows.querySelectorAll('.row').forEach((r) => {
      if (r.querySelector('.rank')?.textContent === String(t.target?.rank)) r.classList.add('target');
    });
    if (t.you && !shown.has(t.you.rank)) {
      if (!t.target) el('div', 'gap', rows, '⋯');
      row(t.you);
    }
    if (t.note) el('div', 'note', c, t.note);
    if (t.challenge) {
      const ch = el('button', 'challenge', c, this.shareSaid ?? 'challenge your friends ↗\uFE0E');
      ch.type = 'button';
      ch.classList.toggle('said', !!this.shareSaid);
      ch.onclick = () => this.onChallenge('card');
    }
    const all = el('button', 'link all', c, 'full leaderboard ▸');
    all.type = 'button';
    all.onclick = () => this.open('board');
  }

  private renderIntro(o: ChallengeIntro): void {
    this.chLabel.textContent = o.label;
    this.chTitle.textContent = o.title ?? 'grand premio';
    this.chPitch.textContent = o.pitch;
    this.chBeatLabel.textContent = o.beat.label ?? 'time to beat';
    this.chCircuitLabel.textContent = o.circuit.label;
    // Generated from the circuit's numbers only.
    this.chOutline.innerHTML = o.circuit.outline;
    this.chCircuit.textContent = o.circuit.name;
    this.chTag.textContent = o.circuit.tag;
    this.chTag.hidden = !o.circuit.tag;
    // The red swatch only where the drawing has its red stretch (circuits from before showpieces don't).
    this.chTag.classList.toggle('marked', o.circuit.outline.includes('class="show"'));
    this.chCarLabel.textContent = o.car.label;
    this.chCar.replaceChildren(`${o.car.name} `);
    el('em', '', this.chCar, String(o.car.year));
    this.chBlurb.textContent = o.car.blurb;
    this.chTime.textContent = o.beat.time;
    this.chWho.textContent = o.beat.who;
    this.chBoard.replaceChildren();
    this.chBoard.hidden = !o.board;
    if (o.board) {
      el('div', 'label', this.chBoard, o.board.title);
      for (const r of o.board.rows) {
        const row = el('div', 'row' + (r.them ? ' them' : ''), this.chBoard);
        el('span', 'rank', row, String(r.rank));
        el('span', 'name', row, r.name);
        el('span', 'time', row, r.time);
      }
    }
    this.chSteps.replaceChildren();
    this.chSteps.hidden = o.steps.length === 0;
    for (const st of o.steps) {
      const li = el('li', '', this.chSteps);
      el('b', '', li, st.title);
      el('span', '', li, st.text);
    }
    this.chNote.textContent = o.note ?? '';
    this.chNote.hidden = !o.note;
    this.chWatch.textContent = o.watch;
    this.chRace.textContent = o.race;
    this.chRaceAction = o.raceAction ?? 'race';
  }

  /** Ask the poll when it's due; once answered it carries on (what's missing, thanks) until it's done. */
  private setPoll(ask: boolean): void {
    if (ask && this.pollState === 'off') this.pollState = 'ask';
    else if (!ask && this.pollState === 'ask') this.pollState = 'off';
    this.renderPoll();
  }

  private renderPoll(): void {
    const row = this.pollRow;
    row.replaceChildren();
    row.hidden = this.pollState === 'off';
    const button = (parent: HTMLElement, label: string, onClick: () => void, cls = 'link'): HTMLButtonElement => {
      const b = el('button', cls, parent, label);
      b.type = 'button';
      b.onclick = onClick;
      return b;
    };
    if (this.pollState === 'ask') {
      button(row, '✕', () => this.answerPoll('skip'), 'close').setAttribute('aria-label', 'not now');
      el('span', 'q', row, 'enjoying grand premio?');
      const answers = el('span', 'answers', row);
      button(answers, 'yes 👍', () => this.answerPoll('up'));
      button(answers, 'not really 👎', () => this.answerPoll('down'));
    } else if (this.pollState === 'why') {
      button(row, '✕', () => {
        this.pollState = 'off';
        this.renderPoll();
      }, 'close').setAttribute('aria-label', 'close');
      el('span', 'q', row, 'thanks · what’s missing?');
      const answers = el('span', 'answers', row);
      for (const [id, label] of POLL_WHY) button(answers, label, () => this.answerPoll(id));
    } else if (this.pollState === 'thanks') {
      el('span', 'said', row, 'thanks! ✓');
    }
  }

  private answerPoll(a: PollAnswer): void {
    this.onPoll(a);
    if (a === 'down') this.pollState = 'why';
    else if (a === 'skip') this.pollState = 'off';
    else {
      this.pollState = 'thanks';
      window.clearTimeout(this.pollTimer);
      this.pollTimer = window.setTimeout(() => {
        this.pollState = 'off';
        this.renderPoll();
      }, 2600);
    }
    this.renderPoll();
  }

  /** Say how the share went ("link copied ✓") for a moment, on every challenge button (poster, card, board). */
  shareFeedback(text: string): void {
    this.shareSaid = text;
    this.refreshChallenge();
    window.clearTimeout(this.shareTimer);
    this.shareTimer = window.setTimeout(() => {
      this.shareTimer = 0;
      this.shareSaid = null;
      this.refreshChallenge();
    }, 2600);
  }

  private refreshChallenge(): void {
    this.board.say(this.shareSaid);
    const o = this.info;
    if (!o) return;
    this.renderProgress();
    this.renderToday(!o.challenge ? (o.today ?? null) : null);
  }

  private styleKey = '';
  private pose: PosterPose | null = null;
  private side = 1;

  /** The day's angle on the car; in the challenge's small "your car" box, a little closer. */
  private posePicture(): void {
    if (!this.pose) return;
    const close = this.view === 'challenge';
    this.art.setPose(close ? { ...this.pose, fill: Math.min(0.9, this.pose.fill * 1.3) } : this.pose, this.side);
  }

  /** The day's print: its colours on the sheet (and its cards), its motif, the car's pose. */
  private applyStyle(st: PosterStyle): void {
    const key = `${st.palette.name}|${st.pose.name}|${st.side}|${st.motif}`;
    if (key === this.styleKey) return;
    this.styleKey = key;
    const p = st.palette;
    const vars: [string, string][] = [
      ['--pf', p.field],
      ['--pt', p.title],
      ['--pi', p.ink],
      ['--px', p.text],
      ['--pa', p.accent],
      ['--pat', p.accentText],
      ['--pm', p.motif],
      ['--ps', p.sun],
    ];
    for (const [k, v] of vars) this.root.style.setProperty(k, v);
    this.poster.dataset.palette = p.name;
    this.motif.className = `motif ${st.motif}${st.side < 0 ? ' flip' : ''}`;
    // The podium band's slanted foot runs the way the day's stripes do.
    this.podiumBand.classList.toggle('flip', st.side < 0);
    this.motif.innerHTML = motifSvg(st.motif, st.side);
    this.pose = st.pose;
    this.side = st.side;
    this.posePicture();
  }

  setSound(muted: boolean): void {
    this.setToggle(this.soundBtn, 'sound', !muted, muted ? GLYPH.soundOff : GLYPH.soundOn);
    this.muteBtn.innerHTML = muted ? GLYPH.soundOff : GLYPH.soundOn;
    this.muteBtn.classList.toggle('off', muted);
    this.muteBtn.setAttribute('aria-label', `Sound: ${muted ? 'off' : 'on'} (M)`);
    this.muteBtn.title = `Sound: ${muted ? 'off' : 'on'} (M)`;
  }

  /** Redraw the illustration (the poster changed size). */
  resize(): void {
    if (this.poster.style.display !== 'none' && (this.view === 'main' || this.view === 'challenge')) this.art.render();
  }

  hide(): void {
    this.root.classList.add('hidden');
    // Once faded out, the poster isn't laid out at all while racing: the board re-rendering behind it
    // (a new best saved) used to cost a hitch at the finish line.
    window.clearTimeout(this.goneTimer);
    this.goneTimer = window.setTimeout(() => {
      if (this.root.classList.contains('hidden')) this.root.classList.add('gone');
    }, 400);
  }

  private goneTimer = 0;

  /**
   * Over the pause menu, for a player whose lap went up under a made-up name: their own name instead?
   * `line`: where it stands ("P8 of 11 · 0:33.964"). Save with what they typed, or keep the made-up one
   * (Esc too).
   */
  askName(o: { name: string; line: string; focus: boolean }, onSave: (name: string) => void, onKeep: () => void): void {
    this.closeAskName();
    const back = el('div', 'name-ask', this.root);
    const card = el('form', 'card', back);
    el('div', 'kicker', card, 'you’re on today’s board');
    el('div', 'as', card, `as ${o.name.toLowerCase()}`);
    el('div', 'where', card, o.line);
    el('div', 'q', card, 'put your own name on it?');
    const line = el('div', 'line', card);
    const input = document.createElement('input');
    input.type = 'text';
    input.value = o.name;
    input.maxLength = NAME_MAX * 2;
    input.setAttribute('autocomplete', 'nickname');
    input.setAttribute('aria-label', 'Your name');
    input.spellcheck = false;
    input.enterKeyHint = 'done';
    line.appendChild(input);
    const save = el('button', 'go', line, 'save') as HTMLButtonElement;
    save.type = 'submit';
    const keep = el('button', 'keep', card, `keep ${o.name.toLowerCase()}`) as HTMLButtonElement;
    keep.type = 'button';
    card.addEventListener('submit', (e) => {
      e.preventDefault();
      onSave(input.value);
    });
    keep.onclick = () => onKeep();
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onKeep();
    });
    this.askEl = back;
    if (o.focus) {
      input.focus();
      input.select();
    }
  }

  closeAskName(): void {
    this.askEl?.remove();
    this.askEl = null;
  }

  get askingName(): boolean {
    return this.askEl !== null;
  }

  /** A browser drawing without the graphics card: one red line on the poster, above the controls, that says how to fix it. */
  showGpuHint(onTap: () => void): void {
    if (this.gpuHint) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gpu-hint interactive';
    b.innerHTML = 'running slowly? your browser is drawing without the graphics card · <u>turn on hardware acceleration ▸</u>';
    b.onclick = onTap;
    this.smallPrint.before(b);
    this.gpuHint = b;
  }

  /**
   * A small card over the poster, for a moment's news (a battle: "marco beat you"; an old link): the poster under
   * it doesn't change. One button, and a quiet way to close it (Esc too). Built as the name card is.
   */
  showPopup(o: {
    kicker: string;
    title: string;
    line?: string | null;
    note?: string | null;
    /** What happens next, step by step (numbered). */
    steps?: string[] | null;
    /** A short silent clip under the title (fetched only now), looping. */
    clip?: { src: string; poster: string; label: string } | null;
    go: string;
    /** The quiet way to close it ('later', 'not now'), or none: the one button closes it. */
    keep?: string | null;
    onGo: () => void;
    onKeep: () => void;
  }): void {
    this.closePopup();
    const back = el('div', 'name-ask popup', this.root);
    const card = el('div', o.clip ? 'card has-clip' : 'card', back);
    el('div', 'kicker', card, o.kicker);
    el('div', 'as', card, o.title);
    if (o.clip) {
      const v = document.createElement('video');
      v.className = 'clip';
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.autoplay = true;
      v.poster = o.clip.poster;
      v.setAttribute('aria-label', o.clip.label);
      v.src = o.clip.src;
      card.appendChild(v);
      void v.play().catch(() => undefined);
    }
    if (o.line) el('div', 'where', card, o.line);
    if (o.note) el('div', 'q', card, o.note);
    if (o.steps?.length) {
      const ol = el('ol', 'steps', card);
      for (const step of o.steps) el('li', '', ol, step);
    }
    const row = el('div', 'row', card);
    const go = el('button', 'go', row, o.go) as HTMLButtonElement;
    go.type = 'button';
    const keep = o.keep ? (el('button', 'keep', card, o.keep) as HTMLButtonElement) : null;
    if (keep) keep.type = 'button';
    const done = (f: () => void) => () => {
      this.closePopup();
      f();
    };
    go.onclick = done(o.onGo);
    if (keep) keep.onclick = done(o.onKeep);
    this.popupPress = (yes) => (yes ? done(o.onGo) : done(o.onKeep))();
    back.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      done(o.onKeep)();
    });
    this.popupEl = back;
    go.focus({ preventScroll: true });
  }

  /**
   * Inside Discord, once for each server: one you run hasn't got the game yet. What it would get there (its own
   * board, its morning paper, best laps posted), and Discord's own add screen with that server already picked.
   * "Not now" or Esc closes it (it comes up again in a week at the soonest, three times at most: src/main.ts).
   */
  showBringPop(o: { server: BringServer; onAdd: () => void; onLater: () => void }): void {
    this.closePopup();
    const back = el('div', 'name-ask popup bring-pop', this.root);
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', `Bring Grand Premio to ${o.server.name}`);
    const card = el('div', 'card', back);
    el('div', 'kicker', card, 'a server of yours');
    const head = el('div', 'bring-head', card);
    serverMark(head, o.server);
    const t = el('div', 'as', head);
    t.append('bring the daily race to ');
    el('span', 'srv-name', t, o.server.name);
    const paper = el('div', 'mini-paper', card);
    el('div', 'mast', paper, 'la gazzetta del grand premio');
    el('div', 'hl', paper, `who wins for ${o.server.name}?`);
    el('div', 'dk', paper, 'every morning, yesterday’s podium in your server');
    // What it gets, and the buttons (beside the rest on a phone held sideways).
    const act = el('div', 'bring-act', card);
    const perks = el('ul', 'perks', act);
    for (const p of ['its own leaderboard, every day', 'everyone’s best lap posted, and who’s on track now']) el('li', '', perks, p);
    const row = el('div', 'row', act);
    const go = el('button', 'go with-mark', row) as HTMLButtonElement;
    go.type = 'button';
    go.innerHTML = `<span class="discord-mark">${GLYPH.discord}</span>`;
    // A long name is in the title already: the button keeps to a few words.
    go.append([...o.server.name].length <= 18 ? `add to ${o.server.name} ▸` : 'add it there ▸');
    const keep = el('button', 'keep', act, 'not now') as HTMLButtonElement;
    keep.type = 'button';
    const done = (f: () => void) => () => {
      this.closePopup();
      f();
    };
    go.onclick = done(o.onAdd);
    keep.onclick = done(o.onLater);
    this.popupPress = (yes) => (yes ? done(o.onAdd) : done(o.onLater))();
    back.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      done(o.onLater)();
    });
    this.popupEl = back;
    go.focus({ preventScroll: true });
  }

  /**
   * Grand Premio on Discord (on the website): a big card over the poster, everything behind it dimmed and blurred.
   * Two screens racing at the same moment (side by side, or in a tall window one over the other: chosen before the
   * clip loads), one line, the add buttons. `isNew`: it came up by itself (once), with "not now"; otherwise from the
   * line over race. ✕, Esc or a tap outside the card closes it; Enter is "add to my server".
   */
  showDiscordPop(o: {
    isNew: boolean;
    onServer: () => void;
    onApps: () => void;
    onClose: () => void;
    /** Il paddock, the Grand Premio server on Discord: a row under the rest; left out: not offered. */
    onPaddock?: () => void;
    /** Signing in with Discord here too (`as`: the name signed in as, or null); left out: not offered. */
    signIn?: { as: string | null } | null;
    onSignIn?: () => void;
    onSignOut?: () => void;
  }): void {
    this.closePopup();
    const back = el('div', 'discord-pop interactive', this.root);
    back.setAttribute('role', 'dialog');
    back.setAttribute('aria-modal', 'true');
    back.setAttribute('aria-label', 'Grand Premio on Discord: race together');
    const card = el('div', 'card', back);
    const x = el('button', 'x', card, '✕') as HTMLButtonElement;
    x.type = 'button';
    x.setAttribute('aria-label', 'Close');
    el('div', 'kicker', card, o.isNew ? 'new · grand premio on discord' : 'grand premio on discord');
    const title = el('h2', '', card);
    title.insertAdjacentHTML('beforeend', `<span class="discord-mark">${GLYPH.discord}</span>`);
    title.append('race together');
    const tall = window.innerWidth < window.innerHeight;
    const v = document.createElement('video');
    v.className = tall ? 'clip tall' : 'clip';
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.autoplay = true;
    v.poster = tall ? 'media/discord-both-tall.jpg' : 'media/discord-both.jpg';
    v.src = tall ? 'media/discord-both-tall.mp4' : 'media/discord-both.mp4';
    v.setAttribute('aria-label', 'Two screens at the same moment, Sara’s and Luca’s: on Sara’s, Luca’s car is ahead, then passed; on Luca’s, Sara’s car comes up from behind and goes by');
    card.appendChild(v);
    void v.play().catch(() => undefined);
    el('p', 'line', card, 'Both on track at the same time, each on your own screen.');
    const add = el('button', 'go with-mark', card) as HTMLButtonElement;
    add.type = 'button';
    add.innerHTML = `<span class="discord-mark">${GLYPH.discord}</span>add to my server ▸`;
    const apps = el('button', 'apps', card, 'no server of your own? add it to your apps') as HTMLButtonElement;
    apps.type = 'button';
    // Already on Discord: sign in here (the same player on every device), or who you're signed in as, and out.
    if (o.signIn) {
      const sign = el('div', 'pop-sign', card);
      if (o.signIn.as === null) {
        const b = el('button', 'sign-in with-mark', sign) as HTMLButtonElement;
        b.type = 'button';
        b.innerHTML = `<span class="discord-mark">${GLYPH.discord}</span>sign in with discord`;
        el('span', 'why', sign, 'already on discord? your laps on every device, and in your servers');
        b.onclick = () => {
          this.closePopup();
          o.onSignIn?.();
        };
      } else {
        const who = el('span', 'who', sign);
        who.insertAdjacentHTML('beforeend', `<span class="discord-mark">${GLYPH.discord}</span>`);
        who.append('signed in as ');
        el('b', '', who, o.signIn.as);
        const out = el('button', 'sign-out', sign, 'sign out') as HTMLButtonElement;
        out.type = 'button';
        let asking = 0;
        out.onclick = () => {
          if (asking) {
            window.clearTimeout(asking);
            this.closePopup();
            o.onSignOut?.();
            return;
          }
          out.textContent = 'sign out?';
          out.classList.add('asking');
          asking = window.setTimeout(() => {
            asking = 0;
            out.textContent = 'sign out';
            out.classList.remove('asking');
          }, 4000);
        };
      }
    }
    // Il paddock: the Grand Premio server itself, for drivers rather than server managers (the paper's own line).
    let join: HTMLButtonElement | null = null;
    if (o.onPaddock) {
      join = el('button', 'pop-paddock', card) as HTMLButtonElement;
      join.type = 'button';
      join.setAttribute('aria-label', 'Il paddock: join the Grand Premio server on Discord');
      join.insertAdjacentHTML('beforeend', `<span class="discord-mark">${GLYPH.discord}</span>`);
      el('b', '', join, 'il paddock');
      el('span', 'words', join, 'talk the races over · the championship is coming');
      el('i', '', join, 'join ▸');
    }
    const later = o.isNew ? (el('button', 'later', card, 'not now') as HTMLButtonElement) : null;
    if (later) later.type = 'button';
    const close = (): void => {
      this.closePopup();
      o.onClose();
    };
    const then = (f: () => void) => (): void => {
      this.closePopup();
      f();
    };
    x.onclick = close;
    if (later) later.onclick = close;
    add.onclick = then(o.onServer);
    apps.onclick = then(o.onApps);
    if (join && o.onPaddock) join.onclick = then(o.onPaddock);
    back.addEventListener('pointerdown', (e) => {
      if (e.target === back) close();
    });
    this.popupPress = (yes) => (yes ? then(o.onServer)() : close());
    this.popupEl = back;
    add.focus({ preventScroll: true });
  }

  closePopup(): void {
    this.popupEl?.remove();
    this.popupEl = null;
    this.popupPress = null;
  }

  /**
   * A stamp over the poster for a moment (the game's own, as a clean lap's: a tilted red box, a line under it):
   * "link copied ✓ · we'll tell you if anyone beats 32.392". Gone after three seconds; nothing to tap.
   */
  stamp(big: string, line: string): void {
    this.stampEl?.remove();
    const s = el('div', 'poster-stamp', this.root);
    el('div', 'big', s, big);
    el('div', 'line', s, line);
    this.stampEl = s;
    window.setTimeout(() => s.classList.add('out'), 2600);
    window.setTimeout(() => {
      s.remove();
      if (this.stampEl === s) this.stampEl = null;
    }, 3100);
  }

  /** The popup's button (true) or its close (false), from a key or a controller. */
  pressPopup(yes: boolean): void {
    this.popupPress?.(yes);
  }

  get showingPopup(): boolean {
    return this.popupEl !== null;
  }

  get visible(): boolean {
    return !this.root.classList.contains('hidden');
  }

}

/**
 * A medal as the poster prints it: a round medal (rim, engraved ring, a flat shine on its face) on a loop,
 * hanging from a short V of ribbon in the tricolore. Its colours, or just its outline, come from main.css.
 * The ribbon's straps lean out from behind the medal and are cut flat by the top of the picture.
 */
const MEDAL = (() => {
  const strap = (deg: number): string =>
    `<g transform="rotate(${deg} 50 52)">` +
    '<rect class="st g" x="38" y="-14" width="8" height="68"/><rect class="st w" x="46" y="-14" width="8" height="68"/><rect class="st r" x="54" y="-14" width="8" height="68"/>' +
    '<rect class="edge" x="38" y="-14" width="24" height="68"/></g>';
  return (
    '<svg viewBox="0 0 100 134" aria-hidden="true">' +
    strap(22) +
    strap(-22) +
    '<circle class="bail" cx="50" cy="40" r="5.5"/>' +
    '<circle class="rim" cx="50" cy="86" r="46"/>' +
    '<circle class="face" cx="50" cy="86" r="39"/>' +
    '<circle class="ring" cx="50" cy="86" r="35.5"/>' +
    '<path class="shine" d="M 20.1 78 A 31 31 0 0 1 42 56.1"/>' +
    '</svg>'
  );
})();

/**
 * The podium band's foot: cut on a slant (deep at one end, nothing at the other) with two racing stripes
 * under the cut, as if the band were the top of the poster's stripes.
 */
const SLANT =
  '<svg class="slant" viewBox="0 0 100 20" preserveAspectRatio="none" aria-hidden="true">' +
  '<polygon class="cut" points="0,0 100,0 100,0.4 0,12"/>' +
  '<polygon class="s1" points="0,13.8 100,2.2 100,4.4 0,16"/>' +
  '<polygon class="s2" points="0,17.6 100,6 100,7.4 0,19"/>' +
  '</svg>';

/** The motif behind the car, as SVG coloured by the day's print (CSS variables). */
function motifSvg(motif: PosterMotif, side: number): string {
  switch (motif) {
    case 'flag':
      return flagSvg();
    case 'sun':
      // A big disc low behind the car, a little off to the side it faces from.
      return `<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true"><circle class="sun" cx="${50 - side * 8}" cy="48" r="36"/></svg>`;
    case 'rays': {
      // A sunburst from below the car, alternate wedges printed.
      const cx = 50;
      const cy = 72;
      let wedges = '';
      const n = 22;
      for (let k = 0; k < n; k += 2) {
        const a0 = (k / n) * Math.PI * 2;
        const a1 = ((k + 1) / n) * Math.PI * 2;
        const R = 160;
        wedges += `<polygon class="ray" points="${cx},${cy} ${(cx + R * Math.cos(a0)).toFixed(1)},${(cy + R * Math.sin(a0)).toFixed(1)} ${(cx + R * Math.cos(a1)).toFixed(1)},${(cy + R * Math.sin(a1)).toFixed(1)}"/>`;
      }
      return `<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">${wedges}</svg>`;
    }
    case 'stripes': {
      // Three racing stripes sweeping across behind the car.
      const lean = side * 26;
      const band = (y: number, h: number, cls: string): string =>
        `<polygon class="${cls}" points="-20,${y + lean} 120,${y - lean} 120,${y - lean + h} -20,${y + lean + h}"/>`;
      return `<svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">${band(30, 9, 'band1')}${band(42, 6, 'band2')}${band(51, 3.5, 'band3')}</svg>`;
    }
  }
}

/**
 * A chequered flag on a pole, the cloth waving: each square is a quad between
 * points of a gently rippled grid. Brown on cream, like the Monza poster.
 */
function flagSvg(): string {
  const cols = 8;
  const rows = 5;
  const sq = 30;
  const x0 = 14;
  const y0 = 10;
  const pt = (c: number, r: number): [number, number] => {
    const u = c / cols;
    // The cloth ripples along its length and droops towards the free end.
    return [x0 + c * sq, y0 + r * sq * (1 - 0.12 * u) + Math.sin(c * 0.9 - 0.4) * 9 * u + u * u * 18];
  };
  const quad = (c: number, r: number): string =>
    [pt(c, r), pt(c + 1, r), pt(c + 1, r + 1), pt(c, r + 1)].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  let checks = '';
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) if ((r + c) % 2 === 0) checks += `<polygon points="${quad(c, r)}"/>`;
  const edge: [number, number][] = [];
  for (let c = 0; c <= cols; c++) edge.push(pt(c, 0));
  for (let r = 0; r <= rows; r++) edge.push(pt(cols, r));
  for (let c = cols; c >= 0; c--) edge.push(pt(c, rows));
  const cloth = edge.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return `<svg viewBox="0 0 270 250" aria-hidden="true">
    <rect class="pole" x="6" y="0" width="7" height="250" rx="3"/>
    <polygon class="cloth" points="${cloth}"/>
    <g class="checks">${checks}</g></svg>`;
}
